import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

// Public, no-login registration page opened from the QR code at an event stall.
// It deliberately renders outside the CRM shell (no auth, trackers or chat).
// Steps: form -> thanks (book a meeting / brochures / go to website) -> book -> booked.
// There is no auto-redirect: the visitor leaves for the website only by tapping the button.

const API_BASE = process.env.REACT_APP_API_URL || "/api";

// Served as static files from the CRM build (frontend/public/brochures).
const BROCHURES = [
  { file: "shine-360-campus.pdf", title: "SHiNE 360 Campus", meta: "20 pages · 8.4 MB" },
  { file: "inc-mandatory-modules.pdf", title: "INC Mandatory Modules Training", meta: "4 pages · 9.1 MB" },
  { file: "osce-solutions.pdf", title: "OSCE Solutions", meta: "4 pages · 2.6 MB" },
];

const NAVY = "#12306F";
const BLUE = "#1B4296";
const TEAL = "#12A4A0";
const CORAL = "#F0605D";
const ICE = "#EAF6F6";

const css = `
.evt-page{min-height:100vh;min-height:100dvh;background:linear-gradient(180deg,${ICE} 0%,#fff 46%);font-family:'Nunito Sans',system-ui,sans-serif;color:${NAVY};display:flex;justify-content:center;padding:0 16px}
.evt-wrap{width:100%;max-width:480px;display:flex;flex-direction:column;min-height:100vh;min-height:100dvh;padding-bottom:28px}
.evt-head{padding:26px 0 16px;text-align:center}
.evt-logo{position:relative;width:264px;height:96px;overflow:hidden;margin:0 auto}
.evt-logo img{position:absolute;width:312px;max-width:none;height:auto;left:-26px;top:-107px}
.evt-hero{background:${NAVY};color:#fff;border-radius:16px;padding:20px 20px 18px;margin-top:2px}
.evt-hero small{display:block;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#8fe3df;font-weight:800}
.evt-hero h1{font-family:'Montserrat',system-ui,sans-serif;font-size:21px;line-height:1.25;margin:6px 0 4px;font-weight:800;text-wrap:balance}
.evt-hero p{margin:0;font-size:13.5px;color:#cfe0ff}
.evt-card{background:#fff;border:1px solid #d9e6ee;border-radius:16px;padding:18px;margin-top:14px;box-shadow:0 6px 24px rgba(18,48,111,.06)}
.evt-field{margin-bottom:14px}
.evt-field label{display:block;font-size:12.5px;font-weight:800;margin-bottom:5px}
.evt-field label i{color:${CORAL};font-style:normal}
.evt-field label span{font-weight:600;color:#6b7fa6}
.evt-in{width:100%;box-sizing:border-box;border:1.5px solid #cbd8e6;border-radius:10px;padding:12px 12px;font-size:16px;font-family:inherit;color:${NAVY};background:#fff;outline:none}
.evt-in:focus{border-color:${BLUE};box-shadow:0 0 0 3px rgba(27,66,150,.14)}
.evt-in.bad{border-color:${CORAL}}
.evt-err{color:#c23b38;font-size:12.5px;margin-top:4px;font-weight:700}
.evt-phone{display:flex;gap:8px}
.evt-phone b{display:flex;align-items:center;padding:0 12px;border:1.5px solid #cbd8e6;border-radius:10px;background:${ICE};font-size:15px}
.evt-pills{display:flex;flex-wrap:wrap;gap:8px}
.evt-pill{border:1.5px solid #cbd8e6;background:#fff;color:${NAVY};border-radius:999px;padding:10px 14px;font-size:14px;font-weight:700;font-family:inherit;cursor:pointer;min-height:44px}
.evt-pill.on{background:${TEAL};border-color:${TEAL};color:#fff}
.evt-btn{width:100%;box-sizing:border-box;border:0;border-radius:12px;background:${CORAL};color:#fff;font-family:'Montserrat',system-ui,sans-serif;font-weight:800;font-size:16px;padding:15px;cursor:pointer;margin-top:4px;text-align:center;text-decoration:none;display:block}
.evt-btn.alt{background:#fff;color:${NAVY};border:2px solid ${NAVY}}
.evt-btn.teal{background:${TEAL}}
.evt-btn:disabled{opacity:.55;cursor:default}
.evt-note{font-size:11.5px;color:#6b7fa6;text-align:center;margin:12px 4px 0;line-height:1.5}
.evt-hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden}

.evt-center{flex:1;display:flex;flex-direction:column;justify-content:center;padding:6px 0 10px}
.evt-done{text-align:center;padding:30px 20px 24px;margin-top:0}
.evt-tick{width:72px;height:72px;border-radius:50%;background:${TEAL};color:#fff;font-size:38px;line-height:72px;margin:0 auto 16px}
.evt-done h2{font-family:'Montserrat',system-ui,sans-serif;font-size:24px;margin:0 0 8px;text-wrap:balance}
.evt-done p{margin:0 0 6px;color:#44587f;font-size:15px;line-height:1.5}
.evt-actions{display:flex;flex-direction:column;gap:12px;margin-top:22px}
.evt-actions .evt-btn{margin-top:0}
.evt-bro{margin-top:22px;text-align:left}
.evt-bro .evt-sec{text-align:center;margin-bottom:4px}
.evt-bro>p{font-size:13px;text-align:center;margin:0 0 12px;color:#6b7fa6}
.evt-bitem{border:1.5px solid #d9e6ee;border-radius:12px;padding:12px;margin-top:10px;background:#fff}
.evt-bitem b{display:block;font-family:'Montserrat',system-ui,sans-serif;font-size:15px;color:${NAVY}}
.evt-bitem small{display:block;font-size:12px;color:#6b7fa6;margin-top:2px;font-weight:700}
.evt-brow{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px}
.evt-bbtn{display:block;text-align:center;text-decoration:none;font-family:inherit;font-weight:800;font-size:14px;border-radius:10px;padding:12px 6px;min-height:44px;box-sizing:border-box;border:2px solid ${TEAL}}
.evt-bbtn.view{background:${TEAL};color:#fff}
.evt-bbtn.get{background:#fff;color:${NAVY}}
.evt-got{display:inline-block;margin:12px auto 0;background:${ICE};border:1.5px solid ${TEAL};color:${NAVY};border-radius:12px;padding:10px 14px;font-weight:800;font-size:14px}

.evt-bhead h2{font-family:'Montserrat',system-ui,sans-serif;font-size:21px;margin:6px 0 4px;text-wrap:balance}
.evt-bhead p{margin:0;color:#44587f;font-size:14px}
.evt-back{align-self:flex-start;background:none;border:0;color:${BLUE};font-weight:800;font-size:14px;padding:8px 0;cursor:pointer;font-family:inherit}
.evt-sec{font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#6b7fa6;margin:0 0 10px}
.evt-month{font-family:'Montserrat',system-ui,sans-serif;font-weight:800;font-size:17px;margin:0 0 12px;color:${NAVY}}
.evt-weeks{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:6px}
.evt-week{border:1.5px solid #cbd8e6;background:#fff;color:${NAVY};border-radius:12px;padding:12px 8px;font-family:inherit;cursor:pointer;display:flex;flex-direction:column;gap:2px;align-items:center;min-height:62px}
.evt-week b{font-family:'Montserrat',system-ui,sans-serif;font-size:15px;font-weight:800}
.evt-week span{font-size:12.5px;font-weight:700;color:#6b7fa6}
.evt-week.on{background:${NAVY};border-color:${NAVY};color:#fff}
.evt-week.on span{color:#cfe0f5}
.evt-week:disabled{background:#f3f6f9;border-color:#f3f6f9;color:#b4c0d0;cursor:default}
.evt-week:disabled span{color:#b4c0d0}
.evt-days{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
.evt-day{border-radius:12px;border:1.5px solid #cbd8e6;background:#fff;color:${NAVY};font-family:inherit;cursor:pointer;padding:10px 0;min-height:64px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px}
.evt-day small{font-size:11.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:#6b7fa6}
.evt-day b{font-family:'Montserrat',system-ui,sans-serif;font-size:20px;font-weight:800;line-height:1}
.evt-day.on{background:${NAVY};border-color:${NAVY};color:#fff}
.evt-day.on small{color:#cfe0f5}
.evt-day:disabled{background:#f3f6f9;border-color:#f3f6f9;color:#b4c0d0;cursor:default}
.evt-day:disabled small{color:#b4c0d0}
.evt-times{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px}
.evt-time{border:1.5px solid #cbd8e6;background:#fff;color:${NAVY};border-radius:10px;padding:11px 0;font-size:14px;font-weight:700;font-family:inherit;cursor:pointer;min-height:44px}
.evt-time.on{background:${TEAL};border-color:${TEAL};color:#fff}
.evt-time:disabled{background:#f3f6f9;border-color:#f3f6f9;color:#b4c0d0;text-decoration:line-through;cursor:default}
.evt-sum{background:${ICE};border-radius:12px;padding:12px 14px;font-size:14px;font-weight:700;margin:4px 0 12px;text-align:center}
.evt-tz{font-size:11.5px;color:#6b7fa6;text-align:center;margin-top:10px}
@media (prefers-reduced-motion:no-preference){.evt-tick{animation:evtpop .35s ease-out}@keyframes evtpop{from{transform:scale(.6);opacity:0}to{transform:scale(1);opacity:1}}}
`;

const EMPTY = { contact_name: "", designation: "", institution_name: "", phone: "", city: "", email: "", student_count: "", notes: "", website: "" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parts = (date) => date.split("-").map(Number);
const dayLabel = (date) => {
  const [y, m, d] = parts(date);
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd}, ${d} ${MONTHS[m - 1].slice(0, 3)} ${y}`;
};
const timeLabel = (t) => {
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

// Splits the bookable days into Monday-start weeks: Week 1, Week 2, ...
function weekGroups(days) {
  const byMonday = new Map();
  days.forEach((d) => {
    const [y, m, n] = parts(d.date);
    const back = (new Date(Date.UTC(y, m - 1, n)).getUTCDay() + 6) % 7; // Monday = 0
    const mon = new Date(Date.UTC(y, m - 1, n - back)).toISOString().slice(0, 10);
    if (!byMonday.has(mon)) byMonday.set(mon, []);
    byMonday.get(mon).push(d);
  });
  return [...byMonday.keys()].sort().map((key, i) => {
    const list = byMonday.get(key);
    const [, m1, d1] = parts(list[0].date);
    const [, m2, d2] = parts(list[list.length - 1].date);
    const range = m1 === m2
      ? `${d1}–${d2} ${MONTHS[m1 - 1].slice(0, 3)}`
      : `${d1} ${MONTHS[m1 - 1].slice(0, 3)} – ${d2} ${MONTHS[m2 - 1].slice(0, 3)}`;
    const open = list.some((d) => d.times.some((t) => t.available));
    return { key, label: `Week ${i + 1}`, range, days: list, open };
  });
}

export default function EventRegister() {
  const slug = window.location.pathname.replace(/^\/event\//, "").split("/")[0];
  const src = new URLSearchParams(window.location.search).get("src") || "";

  const [event, setEvent] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [interests, setInterests] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const [step, setStep] = useState("form"); // form | thanks | book | booked
  const [redirectUrl, setRedirectUrl] = useState("");
    const [reg, setReg] = useState(null); // { id, token }
  const [booked, setBooked] = useState(null); // "YYYY-MM-DD HH:mm"

  const [slots, setSlots] = useState(null);
  const [slotError, setSlotError] = useState("");
  const [pickWeek, setPickWeek] = useState("");
  const [pickDate, setPickDate] = useState("");
  const [pickTime, setPickTime] = useState("");
  const [saving, setSaving] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    document.title = "Event Registration | Ebekmed";
    axios.get(`${API_BASE}/public/events/${encodeURIComponent(slug)}`)
      .then((r) => { setEvent(r.data); document.title = `${r.data.name} | Ebekmed`; })
      .catch((e) => setLoadError(e.response?.data?.error || "This registration page is not available."));
  }, [slug]);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const loadSlots = useCallback(async () => {
    setSlotError("");
    try {
      const r = await axios.get(`${API_BASE}/public/events/${encodeURIComponent(slug)}/slots`);
      setSlots(r.data.days || []);
      return r.data.days || [];
    } catch (e) {
      setSlotError(e.response?.data?.error || "Could not load meeting times. Please try again.");
      return null;
    }
  }, [slug]);

  const openBooking = async () => {
    setStep("book");
    setPickTime("");
    setPickDate("");
    const days = await loadSlots();
    const firstOpen = days && weekGroups(days).find((w) => w.open);
    setPickWeek(firstOpen ? firstOpen.key : "");
  };

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: "" }));
  };
  const toggle = (key) => setInterests((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));

  const phoneDigits = useMemo(() => form.phone.replace(/\D/g, ""), [form.phone]);

  const validate = () => {
    const e = {};
    if (!form.contact_name.trim()) e.contact_name = "Enter your name.";
    if (!form.designation) e.designation = "Choose your designation.";
    if (!form.institution_name.trim()) e.institution_name = "Enter your institution.";
    let p = phoneDigits;
    if (p.length === 12 && p.startsWith("91")) p = p.slice(2);
    if (p.length === 11 && p.startsWith("0")) p = p.slice(1);
    if (!/^[6-9]\d{9}$/.test(p)) e.phone = "Enter a valid 10-digit mobile number.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (busy) return;
    setFormError("");
    const found = validate();
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) { refs.current[first]?.focus(); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/public/events/${encodeURIComponent(slug)}`, {
        ...form, interests, src,
      });
      setRedirectUrl(res.data.redirectUrl || event?.redirectUrl || "https://ebekmed.com");
      setReg(res.data.registrationId && res.data.bookingToken ? { id: res.data.registrationId, token: res.data.bookingToken } : null);
      setBooked(res.data.meetingSlot || null);
      setStep("thanks");
    } catch (err) {
      const data = err.response?.data;
      if (data?.field) { setErrors({ [data.field]: data.error }); refs.current[data.field]?.focus(); }
      else setFormError(data?.error || "We couldn't save your details. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const confirmMeeting = async () => {
    if (!reg || !pickDate || !pickTime || saving) return;
    setSaving(true);
    setSlotError("");
    try {
      await axios.post(`${API_BASE}/public/events/${encodeURIComponent(slug)}/meeting`, {
        id: reg.id, token: reg.token, date: pickDate, time: pickTime,
      });
      setBooked(`${pickDate} ${pickTime}`);
      setStep("booked");
    } catch (err) {
      const data = err.response?.data;
      setSlotError(data?.error || "We couldn't book that time. Please try again.");
      if (data?.taken) { setPickTime(""); await loadSlots(); }
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}, optional = false) => (
    <div className="evt-field">
      <label htmlFor={`evt-${key}`}>{label} {optional ? <span>(optional)</span> : <i>*</i>}</label>
      <input
        id={`evt-${key}`} ref={(el) => { refs.current[key] = el; }}
        className={`evt-in${errors[key] ? " bad" : ""}`} value={form[key]} onChange={set(key)}
        aria-invalid={!!errors[key]} {...props}
      />
      {errors[key] && <div className="evt-err" role="alert">{errors[key]}</div>}
    </div>
  );

  const bookedText = booked ? `${dayLabel(booked.slice(0, 10))} · ${timeLabel(booked.slice(11, 16))} IST` : "";
  const firstName = form.contact_name.trim().split(" ")[0];
  const weeks = useMemo(() => (slots ? weekGroups(slots) : []), [slots]);
  const week = weeks.find((w) => w.key === pickWeek);
  const monthTitle = slots && slots.length
    ? `${MONTHS[parts(slots[0].date)[1] - 1]} ${parts(slots[0].date)[0]}`
    : "";
  const dayTimes = slots?.find((d) => d.date === pickDate)?.times || [];
  const morning = dayTimes.filter((t) => Number(t.time.slice(0, 2)) < 12);
  const afternoon = dayTimes.filter((t) => Number(t.time.slice(0, 2)) >= 12);

  const timeGroup = (title, list) => list.length > 0 && (
    <>
      <div className="evt-sec">{title}</div>
      <div className="evt-times">
        {list.map((t) => (
          <button key={t.time} type="button" disabled={!t.available}
            className={`evt-time${pickTime === t.time ? " on" : ""}`}
            aria-pressed={pickTime === t.time} onClick={() => setPickTime(t.time)}>{timeLabel(t.time)}</button>
        ))}
      </div>
    </>
  );

  const brochures = (
    <div className="evt-bro">
      <div className="evt-sec">Our brochures</div>
      <p>Open them now or save them to your phone.</p>
      {BROCHURES.map((b) => (
        <div className="evt-bitem" key={b.file}>
          <b>{b.title}</b>
          <small>PDF · {b.meta}</small>
          <div className="evt-brow">
            <a className="evt-bbtn view" href={`/brochures/${b.file}`} target="_blank" rel="noopener noreferrer">View</a>
            <a className="evt-bbtn get" href={`/brochures/${b.file}`} download={`${b.title}.pdf`}>Download</a>
          </div>
        </div>
      ))}
    </div>
  );

  let body;
  if (loadError) {
    body = <div className="evt-center"><div className="evt-card evt-done"><h2>Page not found</h2><p>{loadError}</p><div className="evt-actions"><a className="evt-btn" href="https://ebekmed.com">Visit ebekmed.com</a></div></div></div>;
  } else if (!event) {
    body = <div className="evt-center"><div className="evt-card evt-done"><p>Loading…</p></div></div>;
  } else if (step === "thanks") {
    body = (
      <div className="evt-center">
        <div className="evt-card evt-done">
          <div className="evt-tick" aria-hidden="true">✓</div>
          <h2>Thank you, {firstName}!</h2>
          <p>We have your details. Our team will get in touch with you soon.</p>
          {booked && <div className="evt-got">Your meeting: {bookedText}</div>}
          <div className="evt-actions">
            {event.canBook && reg && (
              <button type="button" className="evt-btn" onClick={openBooking}>
                {booked ? "Change meeting time" : "Book a Meeting with the Team"}
              </button>
            )}
          </div>
          {brochures}
          <div className="evt-actions">
            <a className="evt-btn alt" href={redirectUrl}>Go to Website</a>
          </div>
        </div>
      </div>
    );
  } else if (step === "book") {
    body = (
      <>
        <button type="button" className="evt-back" onClick={() => setStep("thanks")}>← Back</button>
        <div className="evt-bhead">
          <h2>Book a meeting with the team</h2>
          <p>Pick a week, a date and a time. A {event.slotMinutes || 30}-minute call, Monday to Saturday.</p>
        </div>
        <div className="evt-card">
          <div className="evt-sec">1 · Choose a week</div>
          {!slots && !slotError && <p style={{ margin: 0 }}>Loading available days…</p>}
          {monthTitle && <div className="evt-month">{monthTitle}</div>}
          <div className="evt-weeks">
            {weeks.map((w) => (
              <button key={w.key} type="button" disabled={!w.open}
                className={`evt-week${pickWeek === w.key ? " on" : ""}`}
                aria-pressed={pickWeek === w.key}
                onClick={() => { setPickWeek(w.key); setPickDate(""); setPickTime(""); }}>
                <b>{w.label}</b><span>{w.range}</span>
              </button>
            ))}
          </div>
        </div>
        {week && (
          <div className="evt-card">
            <div className="evt-sec">2 · Choose a date · {week.label}</div>
            <div className="evt-days">
              {week.days.map((d) => {
                const open = d.times.some((t) => t.available);
                return (
                  <button key={d.date} type="button" disabled={!open}
                    className={`evt-day${pickDate === d.date ? " on" : ""}`}
                    aria-label={dayLabel(d.date)} aria-pressed={pickDate === d.date}
                    onClick={() => { setPickDate(d.date); setPickTime(""); }}>
                    <small>{WEEKDAYS[d.weekday]}</small><b>{parts(d.date)[2]}</b>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {pickDate && (
          <div className="evt-card">
            <div className="evt-sec">3 · Choose a time · {dayLabel(pickDate)}</div>
            {timeGroup("Morning", morning)}
            {timeGroup("Afternoon", afternoon)}
            <div className="evt-tz">All times are Indian Standard Time (IST).</div>
          </div>
        )}
        {slotError && <div className="evt-err" role="alert" style={{ textAlign: "center", marginTop: 12 }}>{slotError}</div>}
        <div className="evt-card" style={{ marginTop: 14 }}>
          {pickDate && pickTime
            ? <div className="evt-sum">{dayLabel(pickDate)} · {timeLabel(pickTime)} IST</div>
            : <div className="evt-sum" style={{ fontWeight: 600, color: "#6b7fa6" }}>Select a week, date and time to continue</div>}
          <button type="button" className="evt-btn teal" disabled={!pickDate || !pickTime || saving} onClick={confirmMeeting}>
            {saving ? "Booking…" : "Confirm meeting"}
          </button>
        </div>
      </>
    );
  } else if (step === "booked") {
    body = (
      <div className="evt-center">
        <div className="evt-card evt-done">
          <div className="evt-tick" aria-hidden="true">✓</div>
          <h2>Meeting booked</h2>
          <p>Our team will call you on</p>
          <div className="evt-got">{bookedText}</div>
          <p style={{ marginTop: 14 }}>We'll ring the number you registered with.</p>
          <div className="evt-actions">
            <button type="button" className="evt-btn alt" onClick={openBooking}>Change time</button>
          </div>
          {brochures}
          <div className="evt-actions">
            <a className="evt-btn" href={redirectUrl}>Go to Website</a>
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        <div className="evt-hero">
          <small>{event.dateLabel}</small>
          <h1>{event.name}</h1>
          <p>Meet the Ebekmed team. Share your details and we'll follow up on SHiNE 360 Campus, INC modules and OSCE.</p>
        </div>
        <form className="evt-card" onSubmit={submit} noValidate>
          {field("contact_name", "Your name", { autoComplete: "name", maxLength: 255 })}
          <div className="evt-field">
            <label htmlFor="evt-phone">Mobile number <i>*</i></label>
            <div className="evt-phone">
              <b>+91</b>
              <input
                id="evt-phone" ref={(el) => { refs.current.phone = el; }}
                className={`evt-in${errors.phone ? " bad" : ""}`} value={form.phone} onChange={set("phone")}
                type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={16} placeholder="98765 43210"
                aria-invalid={!!errors.phone}
              />
            </div>
            {errors.phone && <div className="evt-err" role="alert">{errors.phone}</div>}
          </div>
          <div className="evt-field">
            <label htmlFor="evt-designation">Designation <i>*</i></label>
            <select
              id="evt-designation" ref={(el) => { refs.current.designation = el; }}
              className={`evt-in${errors.designation ? " bad" : ""}`} value={form.designation} onChange={set("designation")}
              aria-invalid={!!errors.designation}
            >
              <option value="">Select…</option>
              {event.designations.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            {errors.designation && <div className="evt-err" role="alert">{errors.designation}</div>}
          </div>
          {field("institution_name", "Institution / College", { autoComplete: "organization", maxLength: 255 })}
          <div className="evt-field">
            <label>Interested in <span>(optional)</span></label>
            <div className="evt-pills">
              {event.interests.map((i) => (
                <button type="button" key={i.key} className={`evt-pill${interests.includes(i.key) ? " on" : ""}`}
                  aria-pressed={interests.includes(i.key)} onClick={() => toggle(i.key)}>{i.label}</button>
              ))}
            </div>
          </div>
          {field("city", "City", { autoComplete: "address-level2", maxLength: 120 }, true)}
          {field("email", "Email", { type: "email", autoComplete: "email", maxLength: 255 }, true)}
          {field("student_count", "Students in your institution", { type: "text", inputMode: "numeric", maxLength: 6 }, true)}
          <div className="evt-field">
            <label htmlFor="evt-notes">Anything you'd like us to know <span>(optional)</span></label>
            <textarea id="evt-notes" className="evt-in" rows={2} maxLength={1000} value={form.notes} onChange={set("notes")} />
          </div>
          {/* Honeypot: real visitors never see or fill this. */}
          <div className="evt-hp" aria-hidden="true">
            <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
          </div>
          {formError && <div className="evt-err" role="alert" style={{ marginBottom: 10 }}>{formError}</div>}
          <button className="evt-btn" type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit"}</button>
          <div className="evt-note">By submitting, you agree to be contacted by Ebekmed about the products you've shown interest in.</div>
        </form>
      </>
    );
  }

  return (
    <div className="evt-page">
      <style>{css}</style>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Nunito+Sans:wght@400;600;700;800&display=swap" />
      <div className="evt-wrap">
        <div className="evt-head"><div className="evt-logo"><img src="/logo-ebekmed.png" alt="Ebekmed" /></div></div>
        {body}
      </div>
    </div>
  );
}
